import type { ColumnRow, Kind, SchemaRow } from './classify'
import { CustomSqlError } from './classify'
import { fold, quoteIdent } from './names'
import { mentions, ownForeignKey, tableConstraint, tableDefinition, tokenize } from './lexer'

export interface Statement { sql: string; params?: unknown[] }
export interface ExpandOptions { columns?: readonly ColumnRow[]; rebuildId?: string }

/** IDs are allocated by the author, then carried verbatim in expanded SQL. */
export function newId(): string {
  const alphabet = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'
  let time = Date.now()
  let prefix = ''
  for (let i = 0; i < 10; i++) { prefix = alphabet[time % 32] + prefix; time = Math.floor(time / 32) }
  return prefix + [...crypto.getRandomValues(new Uint8Array(16))].map(byte => alphabet[byte & 31]).join('')
}

function engineName(name: string): string { return name.replaceAll(/[A-Z]/gu, char => char.toLowerCase()) }

export function expand(kind: Kind, schemaRows: readonly SchemaRow[], options: ExpandOptions = {}): Statement[] {
  if (kind.type === 'index') return []
  if (!kind.rebuild) return [{ sql: kind.sql }]
  const table = schemaRows.find(row => row.type === 'table' && engineName(row.name) === engineName(kind.table ?? ''))
  if (!table?.sql || !kind.column) throw new CustomSqlError('E_SQL', 'Rebuild target is missing')
  const definition = tableDefinition(table.sql)
  let found = false
  const definitions: string[] = []
  for (const part of definition.parts) {
    if ((kind.action === 'drop-column' || kind.action === 'using') && ownForeignKey(part, kind.column)) continue
    if (!tableConstraint(part) && engineName(tokenize(part)[0]?.value ?? '') === engineName(kind.column)) {
      found = true
      if (kind.action !== 'drop-column') definitions.push(`${quoteIdent(kind.newName ?? kind.column)} ${kind.declaration ?? ''}`)
    } else definitions.push(part)
  }
  if (!found) throw new CustomSqlError('E_SQL', 'Column is missing')
  const columns = options.columns
  if (!columns || columns.length === 0) throw new CustomSqlError('E_SQL', 'table_xinfo is required for rebuild')
  const writable = columns.filter(column => column.hidden === 0 && !(kind.action === 'drop-column' && engineName(column.name) === engineName(kind.column ?? '')))
  const rowid = ['rowid', '_rowid_', 'oid'].find(alias => !columns.some(column => fold(column.name) === alias))
  if (!rowid) throw new CustomSqlError('E_TABLE_KIND', 'All rowid aliases are shadowed')
  const temp = `__cdb_rebuild_${options.rebuildId ?? newId()}`
  const targetColumns = writable.map(column => quoteIdent(engineName(column.name) === engineName(kind.column ?? '') ? kind.newName ?? column.name : column.name))
  const sourceColumns = writable.map(column => engineName(column.name) === engineName(kind.column ?? '') && kind.action === 'using' ? `(${kind.expression ?? ''})` : quoteIdent(column.name))
  const views = schemaRows.filter(row => row.type === 'view' && row.sql && mentions(row.sql, table.name))
  const triggers = schemaRows.filter(row => row.type === 'trigger' && row.sql && fold(row.tbl_name) === fold(table.name))
  return [
    ...views.map(view => ({ sql: `DROP VIEW ${quoteIdent(view.name)}` })),
    { sql: `CREATE TABLE ${quoteIdent(temp)} (${definitions.join(', ')})${definition.suffix ? ` ${definition.suffix}` : ''}` },
    { sql: `INSERT INTO ${quoteIdent(temp)} (${[quoteIdent(rowid), ...targetColumns].join(', ')}) SELECT ${[quoteIdent(rowid), ...sourceColumns].join(', ')} FROM ${quoteIdent(table.name)}` },
    { sql: `DROP TABLE ${quoteIdent(table.name)}` },
    { sql: `ALTER TABLE ${quoteIdent(temp)} RENAME TO ${quoteIdent(table.name)}` },
    ...triggers.map(trigger => ({ sql: trigger.sql ?? '' })),
    ...views.map(view => ({ sql: view.sql ?? '' })),
  ]
}

/** Resolved enum attributes belong to the engine; the author carries them into replay. */
export function resolvedTypeExpansion(kind: Kind, resolved: string): Statement[] {
  if (kind.action === 'create') return [{ sql: resolved }]
  const original = tokenize(kind.sql)
  const add = original.findIndex(token => token.type === 'word' && token.value.toUpperCase() === 'ADD')
  const move = original.findIndex(token => token.type === 'word' && token.value.toUpperCase() === 'MOVE')
  const at = add >= 0 ? add : move
  if (at < 0) return [{ sql: kind.sql }]
  const label = original[at + 2]
  if (label?.type !== 'string') throw new CustomSqlError('E_SQL', 'Expected enum value label')
  const part = tableDefinition(resolved).parts.find(part => tokenize(part)[0]?.value === label.value)
  if (!part) throw new CustomSqlError('E_SQL', 'Resolved enum value is missing')
  const tokens = tokenize(part)
  const attributes = tokens.findIndex(token => token.type === 'word' && token.value.toUpperCase() === 'WITH')
  if (attributes < 0) throw new CustomSqlError('E_SQL', 'Engine did not expose resolved enum attributes')
  const body = part.slice(tokens[attributes].end).trim()
  const names = new Set(tokens.slice(attributes + 1).filter(token => token.type === 'word').map(token => token.value.toLowerCase()))
  if (!['id', 'pos', 'color'].every(name => names.has(name))) throw new CustomSqlError('E_SQL', 'Incomplete resolved enum identity')
  const withAt = original.findIndex(token => token.type === 'word' && token.value.toUpperCase() === 'WITH')
  const originalEnd = withAt >= 0 ? original[withAt].start : original.at(-1)?.raw === ';' ? original.at(-1)?.start : kind.sql.length
  const prefix = kind.sql.slice(0, originalEnd).trim()
  if (add >= 0) return [{ sql: `${prefix} WITH ${body}` }]
  const pos = tokens.findIndex(token => token.type === 'word' && token.value.toLowerCase() === 'pos')
  if (tokens[pos + 1]?.raw !== '=' || tokens[pos + 2]?.type !== 'string') throw new CustomSqlError('E_SQL', 'Invalid resolved enum position')
  return [{ sql: `${prefix} WITH (pos = ${tokens[pos + 2].raw})` }]
}
